<template>
    <Button
        class="border-round-xl py-2 px-2 create-queue-btn flex align-items-center justify-content-center gap-2"
        :class="props.class" :loading="appStore.createEmailLoading || appStore.sendEmailBlocked" @click=""
        :data-selectors="JSON.stringify(props.selectors)">
        <i class="pi pi-envelope" v-if="!appStore.sendEmailBlocked && !appStore.createEmailLoading"></i>
        <i class="pi pi-check" v-if="appStore.sendEmailBlocked && !appStore.createEmailLoading"></i>
        <i class="pi pi-spin pi-spinner" v-if="appStore.createEmailLoading"></i>
        <span v-if="props.label" class="create-queue-btn__label">{{ labelText }}</span>
    </Button>
</template>

<script setup>
import { ref, computed, defineProps, watch } from 'vue';
import { useAppStore } from '@/stores/app';

const disabled = ref(false);

const appStore = useAppStore();

const props = defineProps({
    class: {
        type: String,
        default: () => 'js-queue-button',
    },
    selectors: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: () => false,
    },
    blocked: {
        type: Boolean,
        default: () => false,
    },
    // Optional text label (e.g. for the mobile action bar). Icon-only when empty.
    label: {
        type: String,
        default: () => '',
    },
})

// Reflect the action state in the label: idle -> label, sending, sent.
const labelText = computed(() => {
    if (appStore.createEmailLoading) return 'Adding…';
    if (appStore.sendEmailBlocked) return 'Added';
    return props.label;
});

/**
 * Watches the `createEmailLoading` property in the `appStore` and performs actions based on its value.
 * If `isProcessing` is true, sets `sendEmailRequireBlock` to true.
 * If `sendEmailRequireBlock` is false, sets `sendEmailBlocked` to false.
 * Sets `sendEmailBlocked` to true and `sendEmailRequireBlock` to false.
 * After a delay of 10 seconds, sets `sendEmailBlocked` to false.
 */
watch(() => appStore.createEmailLoading, (isProcessing) => {
    if (isProcessing) return appStore.sendEmailRequireBlock = true;
    if (!appStore.sendEmailRequireBlock) return appStore.sendEmailBlocked = false;

    appStore.sendEmailBlocked = true;
    appStore.sendEmailRequireBlock = false;

    setTimeout(() => {
        appStore.sendEmailBlocked = false;
    }, 10000);
});
</script>

<style lang="scss" scoped>
.create-queue-btn {
    background-color: #ffc34e;
    border-color: #ffc34e;
    min-width: 3.75rem;
    min-height: 3.75rem;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.18);
    transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.create-queue-btn>.pi:before {
    font-size: 2rem !important;
    font-weight: 900;
}

.create-queue-btn__label {
    font-size: 1.125rem;
    font-weight: 700;
}

.create-queue-btn,
.clear-all-btn {
    color: #000000 !important;
}

.create-queue-btn:hover:not(:disabled):not(.p-disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.24);
}

.create-queue-btn:active:not(:disabled):not(.p-disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    filter: brightness(0.94);
}

.create-queue-btn:disabled,
.create-queue-btn.p-disabled {
    opacity: 0.65;
    box-shadow: none;
    transform: none;
}
</style>